// ApolloClient.ts - Apollo Client implementation
import { ApolloClient, InMemoryCache, HttpLink, Observable, ApolloLink } from '@apollo/client'
import type { NormalizedCacheObject, Operation } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import fetch from 'cross-fetch'
import { getApiUrl } from '@strata-monorepo/utils/_SiteConfig'
import { jwtDecode } from 'jwt-decode'
import { onError } from '@apollo/client/link/error'

type NetworkError = {
  statusCode?: number
}

type JwtPayload = {
  exp: number
}

type SessionData = {
  source?: string
}

type CohesionData = {
  sessionId: string
  anonymousId: string
  tenantId: string
  writeKey: string
  userAgent: string
  instanceId: string
}

type CreateSessionTokenOptions = {
  data?: SessionData
  refresh?: boolean
  cohesionData?: CohesionData
  site: string
}

// Constants
const LOCAL_STORAGE_TOKEN_KEY = 'power/session-token'
const LOCAL_STORAGE_RO_TOKEN_KEY = 'power/session-ro-token'
const appsyncUrl = `${getApiUrl()}/graphql`
const apiUrl = getApiUrl()
export const READ_ONLY_TOKEN_SOURCE = 'ac87aced-3761-402f-af86-98de0a658e81' // Not a secret

/**
 * Checks if a token is still valid
 * @param {String} token the JWT token
 * @return {Boolean} True if the token is valid and not expired, otherwise false
 */
const tokenValid = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token)
    const currentTime = Date.now() / 1000

    return currentTime < exp
  } catch (error) {
    // TODO: Use Incense to log the error
    return false
  }
}

/**
 * Creates a session token with the provided options
 *
 * @param options Configuration options for session token creation
 * @param options.data Additional data to use for session creation (defaults to source from env)
 * @param options.refresh Whether to force refresh the token even if valid (defaults to false)
 * @param options.cohesionData Cohesion tracking data
 * @param options.site Site identifier (required)
 */
export const createSessionToken = async ({
  data = { source: READ_ONLY_TOKEN_SOURCE },
  refresh = false,
  cohesionData = {
    sessionId: '',
    anonymousId: '',
    tenantId: '',
    writeKey: '',
    userAgent: '',
    instanceId: '',
  },
  site,
}: CreateSessionTokenOptions): Promise<void> => {
  const currentToken = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY)
  if (!refresh && currentToken && tokenValid(currentToken)) {
    return
  }

  if (!data || !data.source) {
    throw new Error('missing session source key')
  }

  /** Source tracking for ESIID Model */
  const isPaidSearch = localStorage.getItem('isPaidSearch') === 'true'

  const url = `${apiUrl.replace(/\/?$/, '')}/session/create`
  const opts = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...data,
      isPaidSearch,
      site,
      cohesion: cohesionData,
      // Kept when the cart library was removed: the session API still receives this
      // value, so changing or dropping it is the session API owner's call.
      cartVersion: 'Flexible Cart',
    }),
  }

  const resp = await fetch(url, opts)
  if (!resp.ok) {
    throw new Error(`expected 201 status code, got ${resp.status}`)
  }

  const { token } = await resp.json()
  localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token)
}

export async function createReadOnlyToken(baseUrl: string, source: string): Promise<void> {
  // Just fetch and store the token - minimal implementation
  const url = `${baseUrl.replace(/\/?$/, '')}/session/read-only?source=${source}`
  const resp = await fetch(url)
  if (!resp.ok) {
    throw new Error(
      `Read only token: Expected successful response (status 2xx), got ${resp.status}`,
    )
  }

  const { token } = await resp.json()
  localStorage.setItem(LOCAL_STORAGE_RO_TOKEN_KEY, token)
}

/**
 * Clears the token from localstorage
 */
export const clearToken = (readOnly: boolean): void => {
  localStorage.removeItem(readOnly ? LOCAL_STORAGE_RO_TOKEN_KEY : LOCAL_STORAGE_TOKEN_KEY)
}

/**
 * Handle expiration is called when an expired token error occurs
 *
 * @param {Operation} operation Apollo operation
 * @param {Function} onExpiration callback to be run on expiration
 * @return {Observable} observable handling the refresh operation
 */
const handleExpiration = (operation: Operation, onExpiration: () => Promise<boolean> | boolean) => {
  return new Observable<Operation>((subscriber) => {
    Promise.resolve(onExpiration())
      .then((retry) => {
        if (retry) {
          subscriber.next(operation)
        }
        subscriber.complete()
      })
      .catch((err) => {
        subscriber.error(err)
      })
  })
}

const onExpiration = (readOnly: boolean): boolean => {
  clearToken(readOnly)
  // TODO: determine if we need to redirect to the home page
  // window.location.href = 'https://www.staging.saveonenergy.com/'
  return false
}

/**
 * Creates a minimal Apollo Client without complex auth features
 */
export function createBasicClient(
  url: string,
  readOnly = true,
): ApolloClient<NormalizedCacheObject> {
  const errorLink = onError(({ networkError, operation, forward, graphQLErrors }) => {
    if (networkError && (networkError as NetworkError).statusCode === 401) {
      return handleExpiration(operation, () => onExpiration(readOnly)).flatMap((op) => forward(op))
    }

    if (graphQLErrors) {
      // TODO: add Incense error reporting for graphQL errors
    } else if (networkError) {
      // TODO: add Incense error reporting for network errors
    }

    return forward(operation)
  })

  // Create the HTTP link
  const httpLink = new HttpLink({
    uri: url,
    fetch,
  })

  // Create the auth link
  const authLink = setContext((_, { headers }) => {
    // Get token from localStorage
    const token = readOnly
      ? localStorage.getItem(LOCAL_STORAGE_RO_TOKEN_KEY)
      : localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY)
    return {
      headers: {
        ...headers,
        Authorization: token || '',
      },
    }
  })

  // Return the Apollo Client instance
  return new ApolloClient({
    link: ApolloLink.from([authLink, errorLink, httpLink]),
    cache: new InMemoryCache(),
    connectToDevTools: false,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  })
}

// Create and export the client instance
const apolloClient = createBasicClient(appsyncUrl)
export const apolloCartClient = createBasicClient(appsyncUrl, false)

export default apolloClient
